import { useState } from 'react';

import { Vehicle } from './useVehicleManagement';
import { ToastType } from './useToast';
import { Customer, Route } from '../../../../api/mobiApi/domain/domain.types';
import { vrpApiService } from '../../../../api/mobiApi/vrp-api.service';

// Manuel rotalama modları
export type ManualRoutingMode =
  | 'multi-move'       // Çoklu taşıma: Birden fazla müşteri seç → araca ata
  | 'area-polygon';    // Çokgen ile alan seçimi

interface UseManualRoutingProps {
  selectedVehicles: Vehicle[];
  selectedCustomers: Customer[];
  routes: Route[];
  depot: { lat: number; lon: number; name: string; demand: number };
  showToast: (type: ToastType, message: string) => void;
  setRoutes: (routes: Route[]) => void;
  setSelectedRoute: (route: Route | null) => void;
  setSelectedCustomers: (customers: Customer[]) => void;
}

export const useManualRouting = ({
  selectedVehicles,
  selectedCustomers,
  routes,
  depot,
  showToast,
  setRoutes,
  setSelectedRoute,
  setSelectedCustomers
}: UseManualRoutingProps) => {
  const [isManualRoutingMode, setIsManualRoutingMode] = useState(false);
  const [manualRoutingMode, setManualRoutingMode] = useState<ManualRoutingMode>('area-polygon');
  const [selectedCustomersForManualRoute, setSelectedCustomersForManualRoute] = useState<Customer[]>([]);
  const [showVehicleSelectionPopup, setShowVehicleSelectionPopup] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleManualRouting = () => {
    setIsManualRoutingMode(!isManualRoutingMode);
    if (!isManualRoutingMode) {
      const modeMessages: Record<ManualRoutingMode, string> = {
        'multi-move': 'Haritadan birden fazla müşteri seçin',
        'area-polygon': 'Harita üzerinde çokgen çizerek alan seçin'
      };
      showToast('info', modeMessages[manualRoutingMode]);
      setSelectedCustomersForManualRoute([]);
    } else {
      showToast('info', 'Manuel rotalama modu kapatıldı');
    }
  };

  const changeManualRoutingMode = (mode: ManualRoutingMode) => {
    setManualRoutingMode(mode);
    const modeNames: Record<ManualRoutingMode, string> = {
      'multi-move': 'Çoklu Taşıma',
      'area-polygon': 'Çokgen Seçimi'
    };
    showToast('info', `${modeNames[mode]} modu aktif`);
    setSelectedCustomersForManualRoute([]);
  };

  // Helper: Drawing mode için map
  const getDrawingMode = (): 'polygon' | null => {
    if (manualRoutingMode === 'area-polygon') return 'polygon';
    return null;
  };

  const handleCustomersSelectedByRectangle = (selectedCusts: Customer[]) => {
    if (selectedCusts.length === 0) {
      showToast('warning', 'Seçili alan içinde müşteri bulunamadı');
      return;
    }

    if (selectedVehicles.length === 0) {
      showToast('warning', 'Lütfen önce araç seçin');
      return;
    }

    setSelectedCustomersForManualRoute(selectedCusts);
    setShowVehicleSelectionPopup(true);
    showToast('success', `${selectedCusts.length} müşteri seçildi`);
  };

  const assignCustomersToVehicle = async (vehicleId: string | number) => {
    try {
      setLoading(true);
      setShowVehicleSelectionPopup(false);

      const depotStr = localStorage.getItem('mobiroute_depot');
      const depotData = depotStr ? JSON.parse(depotStr) : depot;

      const selectedVehicle = selectedVehicles.find(v => v.id === vehicleId);
      if (!selectedVehicle) {
        showToast('error', 'Araç bulunamadı');
        return;
      }

      const normalizedVehicleId = typeof selectedVehicle.id === 'string' && selectedVehicle.id.startsWith('vehicle_')
        ? selectedVehicle.id
        : `vehicle_${selectedVehicle.id}`;

      const selectedCustomerIds = new Set(selectedCustomersForManualRoute.map(c => c.id));

      const cleanedExistingRoutes = routes.map(route => {
        const filteredSequence = route.route_sequence?.filter((step: any) => {
          if (step.location_type === 'DEPOT' || step.location_type === 'depot') {
            return true;
          }
          const customerId = step.customer_id || step.id;
          return !selectedCustomerIds.has(customerId);
        }) || [];

        return {
          ...route,
          route_sequence: filteredSequence
        };
      });

      const editRequest = {
        edits: selectedCustomersForManualRoute.map(c => ({
          customer_lat: c.lat,
          customer_lon: c.lon,
          customer_demand: c.demand || 200,
          customer_id: c.id?.toString() || undefined,
          customer_name: c.name,
          to_vehicle_id: normalizedVehicleId
        })),
        vehicles: [
          {
            id: normalizedVehicleId,
            name: selectedVehicle.name,
            capacity: selectedVehicle.capacity,
            valhallaConfig: {
              costing: 'truck',
              costingOptions: {
                truck: {
                  height: 2.5,
                  width: 2.0,
                  weight: 5
                }
              }
            }
          }
        ],
        existing_routes: cleanedExistingRoutes,
        use_route_optimization: true,
        depot_lat: depotData.lat,
        depot_lon: depotData.lon
      };

      console.log('📤 Manuel rotalama isteği:', editRequest);
      const response = await vrpApiService.editRoute(editRequest);
      console.log('✅ Manuel rotalama yanıtı:', response);

      if (response.data?.cars && response.data.cars.length > 0) {
        const updatedRoutes = response.data.cars;
        const newRoute = updatedRoutes.find(r => r.vehicle_id === normalizedVehicleId);

        if (newRoute) {
          if (!newRoute.raw_shapes || newRoute.raw_shapes.length === 0) {
            console.warn('⚠️ Yeni rota için raw_shapes boş!');
            showToast('warning', 'Rota oluşturuldu ancak yol bilgisi eksik');
          }
        }

        // Add new customers to selectedCustomers
        const existingCustomerIds = new Set(selectedCustomers.map(c => c.id));
        const newCustomersToAdd = selectedCustomersForManualRoute.filter(
          c => !existingCustomerIds.has(c.id)
        );

        if (newCustomersToAdd.length > 0) {
          setSelectedCustomers([...selectedCustomers, ...newCustomersToAdd]);
          console.log(`✅ ${newCustomersToAdd.length} yeni müşteri haritaya eklendi`);
        }

        setRoutes(updatedRoutes);

        if (newRoute) {
          setTimeout(() => {
            setSelectedRoute(newRoute);
            console.log('✅ Seçili rota güncellendi:', newRoute.vehicle_id);
          }, 100);
        }

        showToast('success', 'Manuel rota başarıyla oluşturuldu');
        return true;
      } else {
        showToast('error', 'Rota oluşturulamadı');
        return false;
      }
    } catch (error: any) {
      console.error('❌ Manuel rotalama hatası:', error);
      showToast('error', 'Manuel rotalama sırasında hata: ' + error.message);
      return false;
    } finally {
      setLoading(false);
      setIsManualRoutingMode(false);
      setSelectedCustomersForManualRoute([]);
    }
  };

  return {
    isManualRoutingMode,
    manualRoutingMode,
    drawingMode: getDrawingMode(),
    selectedCustomersForManualRoute,
    showVehicleSelectionPopup,
    loading,
    toggleManualRouting,
    changeManualRoutingMode,
    handleCustomersSelectedByRectangle,
    assignCustomersToVehicle,
    setShowVehicleSelectionPopup
  };
};
